import { Injectable } from "@angular/core";
import { ReplaySubject } from "rxjs";

/**
 * Issue #564 (round 2): a true app-wide singleton for the "has the server
 * actually confirmed this session?" signal — deliberately NOT a member of
 * AuthService.
 *
 * `CoreModule` (which provides `AuthService`) is imported by BOTH the root
 * `AppModule` and the lazily-loaded `DashboardModule` (see
 * `dashboard.module.ts` / `app.routing.ts`). A lazy-loaded module gets its
 * own child injector, so a provider declared in a module's `providers`
 * array — as `AuthService` is, on `CoreModule` — is instantiated ONCE PER
 * INJECTOR: one `AuthService` for the root injector (and everything that
 * resolves from it, e.g. `GlobalOverlayComponent`, mounted at the app root)
 * and a SEPARATE `AuthService` instance for `DashboardModule`'s lazy
 * injector (and everything resolved from it, e.g. `DashboardComponent`).
 * `AuthInterceptor` (root-provided) only ever confirms the root instance,
 * so a `sessionConfirmed$` living on `AuthService` left `DashboardComponent`
 * listening to a subject that could never emit — on a FRESH in-app sign-in
 * (no full reload) the walk-me modal's dashboard-side auto-open trigger
 * silently went dead, while a hard reload of `/flyboard` (which re-mounts
 * `GlobalOverlayComponent` off the same, now-confirmed root instance)
 * masked the bug.
 *
 * `@Injectable({ providedIn: "root" })` sidesteps this entirely: Angular
 * guarantees exactly one instance of a root-provided service across every
 * injector in the app, lazy-loaded or not. Do NOT move this into
 * `CoreModule`'s `providers` array (or any other module's) — that
 * reintroduces the exact two-instance bug this service exists to fix.
 */
@Injectable({ providedIn: "root" })
export class SessionConfirmationService {
  /**
   * Emits once this app instance has completed its first authenticated
   * (Authorization header present) 2xx round-trip to a non-exempt endpoint
   * — i.e. the server has actually confirmed the session, not just that
   * localStorage still holds cached `userDetails`/tokens from a previous,
   * possibly now expired, sign-in. Emitted by `AuthInterceptor` via
   * {@link confirmSession}. ReplaySubject(1) so a component created after
   * the confirming request already resolved (the common case —
   * global-overlay/dashboard mount before any API call lands) still gets
   * the signal immediately.
   */
  readonly sessionConfirmed$ = new ReplaySubject<true>(1);
  private sessionConfirmedEmitted = false;

  /**
   * Called by `AuthInterceptor` on the first authenticated 2xx response of
   * this app instance. Idempotent — only the first call emits, so the
   * interceptor doesn't need to track state itself and later (redundant)
   * authenticated responses are no-ops.
   */
  confirmSession(): void {
    if (this.sessionConfirmedEmitted) {
      return;
    }
    this.sessionConfirmedEmitted = true;
    this.sessionConfirmed$.next(true);
  }
}
